<template>
  <div class="flex max-w-full h-full">
    <div class="flex-1 flex flex-col" v-if="editor">
      <EditorMenubar :editor="editor" @save="handleSave" @show-h-t-m-l="showHTML = !showHTML" @show-j-s-o-n="showJSON = !showJSON" />
      <BubbleMenu :editor="editor" v-if="editor && !showTemplateVariablesEditor" :tippy-options="{ duration: [100, 0] }">
        <div class="bubble-menu">
          <button @click="editor.chain().focus().toggleBold().run()" :class="{ 'is-active': editor.isActive('bold') }">Bold</button>
          <button @click="editor.chain().focus().toggleItalic().run()" :class="{ 'is-active': editor.isActive('italic') }">Italic</button>
          <button @click="editor.chain().focus().toggleUnderline().run()" :class="{ 'is-active': editor.isActive('underline') }">
            Underline
          </button>
          <button @click.stop="startVariableEditing" class="relative">
            Variable
            <div class="absolute top-[1.7em] left-0 z-50" v-if="showVariablesDropdown" v-click-outside="stopVariableEditing">
              <TemplateVariablesDropdownWrapper
                :variables="templateVariables"
                :editor="editor"
                @set-variable="handleSetVariable"
                @stop-editing="stopVariableEditing"
                @add-variable="handleAddVariable"
              />
            </div>
          </button>
        </div>
      </BubbleMenu>
      <div class="h-full bg-white w-[210mm] m-auto mt-4 shadow-sm">
        <EditorContent :editor="editor" class="editor-wrapper" />
      </div>
    </div>
    <div
      class="absolute top-0 left-0 w-full h-full bg-black/50 z-50 flex justify-center items-center"
      v-if="showTemplateVariablesEditor"
      @click="showTemplateVariablesEditor = false"
    >
      <div class="w-[70vw] min-w-[250px] min-h-[1/2] overflow-y-auto p-5 border bg-white rounded-lg" @click.stop>
        <TemplateVariableForm :template-variable="{} as TemplateVariable" @submit="handleVariableFormSubmit" />
      </div>
    </div>
    <div
      v-if="showHTML || showJSON"
      class="max-h-[90%] max-w-[90%] w-full right-[5%] top-[5%] absolute flex flex-col bg-zinc-600 z-50 p-10"
    >
      <div class="pb-5 inline-flex w-full">
        <a class="cursor-pointer border-2 bg-white p-2 mr-2" @click="toggleHtmlAndJson">
          {{ showHTML ? 'show json' : 'show html' }}
        </a>
        <a class="cursor-pointer border-2 bg-white p-2 mr-2 ml-auto" @click.prevent="closeDebugger">Close</a>
      </div>
      <div class="overflow-y-scroll">
        <code :class="showHTML ? '' : 'hidden'">
          <pre class="text-white bg-slate-900 px-5 py-10 rounded-lg whitespace-pre-wrap">{{ editor?.getHTML() }} </pre>
        </code>
        <JsonPrettier :data="modelValue" :class="showJSON ? '' : 'hidden'" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, provide } from 'vue'
import tippy, { type GetReferenceClientRect, type Instance } from 'tippy.js'
import { onBeforeRouteLeave } from 'vue-router'

import { useEditor, EditorContent, VueRenderer, BubbleMenu, type JSONContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Document from '@tiptap/extension-document'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { ExtensionCondition, ExtensionTemplateVariable, ExtensionSection } from './extensions'

import SuggestionList from './SuggestionList.vue'
import type { SuggestionOptions } from '@tiptap/suggestion'
import { useAdminStore } from '../../../admin/adminStore'
import { storeToRefs } from 'pinia'
import type { Template } from '../../../admin/types'
import EditorMenubar from './EditorMenubar.vue'
import JsonPrettier from './JsonPrettier.vue'
import TemplateVariableForm from './TemplateVariableForm.vue'
import TemplateVariablesDropdownWrapper from '@/questionnaire/components/TemplateVariablesDropdownWrapper.vue'
import type { TemplateVariable } from '@/questionnaire/types'
import type { FormKitNode } from '@formkit/core'
import { useSectionValidation } from './composables/useSectionValidation'
import { errorNotification } from '@/utils/notifications'
const props = defineProps<{
  modelValue: Template
}>()
const emit = defineEmits<{
  'update:modelValue': [modelValue: JSONContent]
  save: []
}>()
const showHTML = ref(false)
const showJSON = ref(false)

const adminStore = useAdminStore()
const { templateVariables } = storeToRefs(adminStore)

const showTemplateVariablesEditor = ref(false)
const showVariablesDropdown = ref(false)
const dropdownSelectionRange = ref<{ from: number; to: number } | null>(null)
const nodeViewEditingPosition = ref<number | null>(null)

// Section validation
const sectionValidation = useSectionValidation()
provide('sectionValidation', sectionValidation)

// FIXME: when templateVariables is updated their references aren't updated in the editor.
// maybe reference by id instead of code and look up the display value

const handleVariableFormSubmit = (data: TemplateVariable, node?: FormKitNode) => {
  return adminStore
    .saveTemplateVariable(data)
    .then(response => {
      if (nodeViewEditingPosition.value !== null && editor.value) {
        // Update the existing node view that triggered the creation
        const pos = nodeViewEditingPosition.value
        editor.value.chain().focus().setNodeSelection(pos).updateAttributes('templateVariable', { name: response.code }).run()
      } else {
        // Normal insertion at current position
        editor.value?.commands.setTemplateVariable(response.code)
      }
      // Close the modal after successful creation
      showTemplateVariablesEditor.value = false
      // Reset the node view position
      nodeViewEditingPosition.value = null
    })
    .catch(err => {
      if (err.response.data?.errors) {
        node?.setErrors(err.response.data.errors)
      }
    })
}

const suggestion: Partial<SuggestionOptions> = {
  items: ({ query }) => {
    return templateVariables.value
      .map(item => item.code)
      .filter(code => code.toLowerCase().startsWith(query.toLowerCase()))
      .slice(0, 5)
  },

  render: () => {
    let component: VueRenderer
    let popup: Instance | undefined

    return {
      onStart: props => {
        component = new VueRenderer(SuggestionList, {
          props,
          editor: props.editor,
        })

        if (!props.clientRect) {
          return
        }
        popup = tippy(document.createElement('div'), {
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
          appendTo: () => document.body,
          content: component.element || undefined,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          zIndex: 40,
          placement: 'bottom-start',
        })
      },

      onUpdate(props) {
        component.updateProps(props)

        if (!props.clientRect) {
          return
        }

        popup?.setProps({
          getReferenceClientRect: props.clientRect as GetReferenceClientRect,
        })
      },

      onKeyDown(props) {
        if (props.event.key === 'Escape') {
          popup?.hide()
          return true
        }

        return component.ref?.onKeyDown(props)
      },

      onExit() {
        popup?.destroy()
        component.destroy()
      },
    }
  },
}

const SectionDocument = Document.extend({
  content: 'section+',
  allowGapCursor: true,
})
const hasUnsavedChanges = ref(false)
const editor = useEditor({
  editorProps: {
    attributes: {
      class: 'prose w-full focus:outline-none border border-slate p-5 max-w-none',
    },
  },
  content: props.modelValue.definition,
  extensions: [
    SectionDocument,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    StarterKit.configure({
      document: false,
    }),
    ExtensionCondition,
    Underline,
    ExtensionSection,
    ExtensionTemplateVariable.configure({
      suggestion,
      templateVariables,
    }),
  ],
  onUpdate: () => {
    hasUnsavedChanges.value = true
    emit('update:modelValue', { ...props.modelValue, definition: editor.value?.getJSON() })
  },
  autofocus: true,
})

onBeforeRouteLeave((to, from, next) => {
  if (hasUnsavedChanges.value) {
    if (confirm('You have unsaved changes. Do you want to leave?')) {
      next()
    } else {
      next(false)
    }
  } else {
    next()
  }
})

onBeforeUnmount(() => {
  if (editor.value?.view.dom) {
    editor.value.view.dom.removeEventListener('open-template-variable-editor', openTemplateVariableEditor as (event: Event) => void)
    editor.value.view.dom.removeEventListener('open-template-variables-dropdown', openTemplateVariablesDropdown as (event: Event) => void)
  }
  editor.value?.destroy()
})

onMounted(() => {
  editor.value?.commands?.setContent(editor.value?.getJSON())

  // Listen for the custom open-template-variable-editor event
  if (editor.value?.view.dom) {
    editor.value.view.dom.addEventListener('open-template-variable-editor', openTemplateVariableEditor as (event: Event) => void)
    editor.value.view.dom.addEventListener('open-template-variables-dropdown', openTemplateVariablesDropdown as (event: Event) => void)
  }
})

const startVariableEditing = () => {
  if (!editor.value) return

  // Store the current selection
  const { from, to } = editor.value.state.selection
  dropdownSelectionRange.value = from !== to ? { from, to } : null

  // Show the dropdown
  showVariablesDropdown.value = true
}

const stopVariableEditing = () => {
  showVariablesDropdown.value = false
  dropdownSelectionRange.value = null
}

const handleSetVariable = (code: string) => {
  if (!editor.value) return

  if (dropdownSelectionRange.value) {
    // Replace the selected text with the template variable
    editor.value
      .chain()
      .focus()
      .deleteRange(dropdownSelectionRange.value)
      .insertContent({ type: 'templateVariable', attrs: { name: code } })
      .run()
  } else {
    // Just insert at current position
    editor.value.commands.setTemplateVariable(code)
  }

  stopVariableEditing()
}

const handleAddVariable = () => {
  stopVariableEditing()
  showTemplateVariablesEditor.value = true
}

const openTemplateVariablesDropdown = (_event: Event) => {
  // This event is still dispatched by the extension but we handle it differently now
  // through the bubble menu button click
}

const openTemplateVariableEditor = (event: Event) => {
  const detail = (event as CustomEvent).detail
  const position = detail?.position
  const isNodeView = detail?.isNodeView

  if (isNodeView && position !== undefined) {
    // Store the position of the node view that triggered the creation
    nodeViewEditingPosition.value = position
  } else {
    // Regular creation from toolbar or other source
    nodeViewEditingPosition.value = null
  }

  showTemplateVariablesEditor.value = true
}

const toggleHtmlAndJson = () => {
  showJSON.value = !showJSON.value
  showHTML.value = !showHTML.value
}

const closeDebugger = () => {
  showJSON.value = false
  showHTML.value = false
}

const handleSave = () => {
  if (!editor.value) return

  // Validate all sections before saving
  const isValid = sectionValidation.validateSections(editor.value)

  if (!isValid) {
    const errorMessage = sectionValidation.getValidationSummary()
    errorNotification(`Cannot save template: ${errorMessage}`)
    return
  }

  // Clear any previous validation errors
  sectionValidation.clearValidationErrors()
  hasUnsavedChanges.value = false
  emit('save')
}
</script>

<style>
.template-variable {
  @apply text-white border rounded-md px-1 border-gray-500 bg-gray-700;
}

.bubble-menu {
  @apply bg-white border border-gray-200 rounded-md shadow-lg flex p-1 gap-x-1 text-sm;

  button {
    @apply p-1 rounded-md;
    &:hover {
      @apply bg-gray-300;
    }

    &.is-active {
      @apply bg-primary-200 font-bold;

      &:hover {
        @apply bg-primary-300;
      }
    }
  }
}
</style>
